import React, { useCallback, useEffect } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Anchor, xcss } from '@atlaskit/primitives';

import { OnboardingNudge } from '@confluence/onboarding-nudge-tooltip/entry-points/OnboardingNudgeTooltip';
import { NOTES_ONBOARDING_SPOTLIGHT } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';

export interface NotesOnboardingSpotlightProps {
	shouldShow: boolean;
	onOkAction?: () => void;
	onTryItAction?: () => void;
	onOutsideClick?: () => void;
	children: React.ReactNode;
}

export const NotesOnboardingSpotlight = ({
	shouldShow = false,
	onOkAction,
	onTryItAction,
	onOutsideClick,
	children,
}: NotesOnboardingSpotlightProps) => {
	const { setOnboardingState } = useSetOnboardingState();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleDismiss = useCallback(() => {
		onOkAction?.();
		setOnboardingState({
			key: NOTES_ONBOARDING_SPOTLIGHT.SPOTLIGHT_DISMISSED,
			value: 'true',
		});
	}, [onOkAction, setOnboardingState]);

	const handleTryItClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'tryIt',
				source: 'notesOnboardingSpotlight',
			},
		}).fire();
		onTryItAction?.();
		handleDismiss();
	}, [onTryItAction, createAnalyticsEvent, handleDismiss]);

	const handleOkClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismiss',
				source: 'notesOnboardingSpotlight',
			},
		}).fire();
		handleDismiss();
	}, [createAnalyticsEvent, handleDismiss]);

	const handleLearnMoreClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'learnMore',
				source: 'notesOnboardingSpotlight',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const handleOutsideClick = useCallback(() => {
		handleDismiss();
		onOutsideClick?.();
	}, [handleDismiss, onOutsideClick]);

	useEffect(() => {
		if (shouldShow) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'shown',
					actionSubject: 'nudgeTooltip',
					actionSubjectId: 'notesOnboarding',
					source: 'notesOnboardingSpotlight',
				},
			}).fire();
		}
	}, [createAnalyticsEvent, shouldShow]);

	return (
		<OnboardingNudge
			hidden={!shouldShow}
			setHidden={() => {}}
			heading={formatMessage(i18n.heading)}
			actions={[
				{
					onClick: handleTryItClick,
					text: formatMessage(i18n.tryItButton),
				},
				{
					onClick: handleOkClick,
					text: formatMessage(i18n.okButton),
				},
			]}
			content={
				<FormattedMessage
					{...i18n.body}
					values={{
						a: (chunks: React.ReactNode[]) => (
							<Anchor
								href="https://support.atlassian.com/confluence-cloud/docs/create-and-edit-pages/"
								target="_blank"
								onClick={handleLearnMoreClick}
								xcss={linkStyles}
								tabIndex={-1}
							>
								{chunks}
							</Anchor>
						),
					}}
				/>
			}
			actionSubjectId="notesOnboarding"
			source="notesOnboardingSpotlight"
			width={275}
			position="right-start"
			hideNudgeOnClick={false}
			hideSpotlightCardOnOutsideClick={false}
			onOutsideClick={handleOutsideClick}
		>
			{children}
		</OnboardingNudge>
	);
};

const linkStyles = xcss({
	textDecoration: 'underline',
	cursor: 'pointer',
	color: 'color.text.inverse',
	':hover': {
		color: 'color.text.inverse',
	},
});

const i18n = defineMessages({
	heading: {
		id: 'onboarding-notes.introSpotlight.heading',
		description: 'heading for the notes onboarding spotlight',
		defaultMessage: 'Take notes in private',
	},
	body: {
		id: 'onboarding-notes.introSpotlight.body',
		description: 'body for the notes onboarding spotlight',
		defaultMessage: '<a>Jot down ideas</a> or create to-dos that no one can see but you.',
	},
	okButton: {
		id: 'onboarding-notes.introSpotlight.okButton',
		description: 'CTA OK button for the notes onboarding spotlight',
		defaultMessage: 'Ok',
	},
	tryItButton: {
		id: 'onboarding-notes.introSpotlight.tryItButton',
		description: 'CTA try it button for the notes onboarding spotlight',
		defaultMessage: 'Try it',
	},
});
